/**
 * This is a hardcoded environment file that decides the current environment.
 * A decision is made based on the Country that is initialized by the client.
 * All data here is hardcoded, in future this will be converted to APIs.
 */
import type { SupportedLanguages } from '@/locales/languages';
import { LANGUAGES } from '@/locales/languages';
import { Country, TransactionType } from './config';
import { InitializeOptions } from './types';
import { MaintenanceWindowInput } from './MaintenanceLib';
import {
  getMaintenanceMessageFromConfig,
  FOOTBALL_COM_MAINTENANCE_MESSAGES,
  SPORTYBET_MAINTENANCE_MESSAGES,
} from './environment.meta';
import { devLog } from './debug';

declare const GLOBAL_IS_PROD: boolean;
declare const GLOBAL_ENV_NAME: 'FOOTBALL_PROD' | 'FOOTBALL_STAGE' | 'SPORTY_PRODUCTION';

export interface FirebaseSetting {
  auth: string;
  database: string;
  projectId: string;
}

type LanguageSpec = SupportedLanguages[] | 'ALL_LANGUAGES';
type MessageFactory = Partial<
  Record<Country, { message: Record<string, { strong: string; weak: string }>; email: string }>
>;
export interface HardCodedEnvironment {
  readonly url: string;
  readonly countryToBrand?: Record<Country, string>;
  readonly defaultBrand: string;
  readonly firebase: Readonly<FirebaseSetting>;
  readonly userFieldType?: Partial<Record<Country, 'phone' | 'email'>>;
  readonly languages: Record<Country, LanguageSpec>;
  readonly appName: string;
  readonly features?: Record<string, boolean>;
  readonly customisationAbPercentage?: Partial<Record<Country, number>>;
  readonly landingEntryAbPercentage?: Partial<Record<Country, number>>;
  /** Host user ids (initForeignId) that always get the new chatbot UI, bypassing the customisation A/B test. */
  readonly newChatbotFlowBypassUserIds?: string[];
  readonly gaId?: string;
  readonly app?: {
    deepLinkShortcut?: string;
    deepLinkUrl?: string;
    minVersionSupport?: Record<string, { MIN_IOS?: string; MIN_APP?: string }>;
    nativeCallSupport?: Partial<Record<Country, boolean>>;
    /** When true (with `platform === 'app'`), microphone access is checked via native parent bridge instead of getUserMedia. */
    nativeMicroAccessCheck?: boolean;
    abandonedCSATSupport?: Partial<Record<Country, boolean>>;
  };
  readonly default?: Record<string, any>;
  readonly AI?: {
    rateResponse?: boolean;
    bypassAbTestingUserIds?: string[];
    showInBrowseOtherArticles?: boolean;
    skipAi?: {
      sectionIds?: string[];
      categoryIds?: string[];
    };
  };
  readonly selectedEntityCategory?: Partial<Record<TransactionType, Partial<Record<Country, string>>>>;
  readonly defaultOpenRequestResponseTime?: number;
  readonly maintenanceMode?: {
    enabled: boolean;
    window?: MaintenanceWindowInput;
    messages?: MessageFactory;
  };
}

const DEFAULT_LANGUAGE_MAPPING: Partial<Record<Country, LanguageSpec>> = {
  [Country.ZA]: ['en', 'af-za'],
  [Country.KE]: ['en', 'sw'],
  [Country.TZ]: ['en', 'sw'],
  [Country.CM]: ['en', 'fr', 'fr-cm'],
  [Country.INT]: ['en', 'es', 'fr', 'pt'],
};

/**
 * Enable a list of languages that can be switched from the chatbot.
 * @param languages Optionally override the language specifications.
 */
function enableLanguages(languages: Partial<Record<Country, LanguageSpec>> = {}): Record<Country, LanguageSpec> {
  return {
    [Country.NG]: ['en'],
    [Country.KE]: ['en'],
    [Country.GH]: ['en'],
    [Country.TZ]: ['en'],
    [Country.UG]: ['en'],
    [Country.ZM]: ['en'],
    [Country.INT]: ['en'],
    [Country.CM]: ['en'],
    [Country.ZA]: ['en'],
    [Country.BR]: ['en', 'pt'],
    [Country.MX]: ['en', 'es-mx'],
    [Country.MZ]: ['en', 'pt-mz'],
    ...DEFAULT_LANGUAGE_MAPPING,
    ...languages,
  };
}

// Environments
const STAGING: HardCodedEnvironment = {
  url: 'https://stage.pleased2.com',
  countryToBrand: {
    [Country.NG]: 'sportybetng-uat',
    [Country.KE]: 'sportybetke-uat',
    [Country.GH]: 'sportybetgh-uat',
    [Country.TZ]: 'sportybettz-uat',
    [Country.UG]: 'sportybetug-uat',
    [Country.ZM]: 'sportybetzm-uat',
    [Country.INT]: 'sportybetint-uat',
    [Country.CM]: 'sportybetcm-uat',
    [Country.ZA]: 'sportybetza-uat',
    [Country.BR]: 'sportybetbr-uat',
    [Country.MX]: 'sportybetmx-uat',
    [Country.MZ]: 'sportybetmz-uat',
  },
  defaultBrand: 'sportybetng-uat',
  firebase: {
    auth: 'pleased-firebase-uat.firebaseapp.com',
    database: 'https://pleased-fb-uat-stage.firebaseio.com',
    projectId: 'pleased-firebase-uat',
  },
  userFieldType: {
    [Country.INT]: 'email',
    [Country.BR]: 'email',
    [Country.MX]: 'email',
  },
  languages: enableLanguages({ [Country.UG]: 'ALL_LANGUAGES', [Country.INT]: ['en', 'pt'] }),
  appName: 'SportyBet',
  features: {
    remedy: true,
    kycCheck: true,
    chatTriggerOpenRequestDescriptionBox: true,
    showTransactionSelectorNoneOption: true,
    showTransactionSelectorNoneOptionForAI: true,
    showTransactionSelectorNoneOptionForMetaAuto: false,
    customisation: true,
  },
  gaId: 'G-H7LZWSGKL9',
  app: {
    nativeMicroAccessCheck: true,
    deepLinkShortcut: 'sportybet://',
    deepLinkUrl: 'https://www.test.easebet.com',
    minVersionSupport: {
      articleActions: {
        MIN_IOS: '1.98.0',
        MIN_APP: '1.21.81',
      },
      articleActionsV2: {
        MIN_IOS: '1.149.0',
        MIN_APP: '1.21.177',
      },
      voiceMinimize: {
        MIN_IOS: '1.150.0',
        MIN_APP: '1.21.177',
      },
      nativeCallSupport: {
        MIN_APP: '1.42.0',
      },
    },
    nativeCallSupport: {
      [Country.NG]: true,
    },
    abandonedCSATSupport: {
      [Country.NG]: true,
    },
  },
  default: {
    userName: 'Sporty User',
  },
  AI: {
    rateResponse: true,
    bypassAbTestingUserIds: ['3379015', '3382641'],
  },
  selectedEntityCategory: {
    [TransactionType.Bet]: {
      [Country.NG]: '883',
      [Country.UG]: '868',
    },
  },
  maintenanceMode: {
    enabled: true,
    window: {
      startDate: '2026-05-25T13:49:00Z',
      endDate: '2026-05-30T00:00:00Z',
      id: 'firebase-migration-window-1',
    },
    messages: SPORTYBET_MAINTENANCE_MESSAGES,
  },
};

const UAT1: HardCodedEnvironment = {
  url: 'https://uat1.pleased2.com',
  defaultBrand: 'sportybetng-uat1',
  countryToBrand: {
    [Country.NG]: 'sportybetng-uat1',
    [Country.KE]: 'sportybetke-uat1',
    [Country.GH]: 'sportybetgh-uat1',
    [Country.TZ]: 'sportybettz-uat1',
    [Country.UG]: 'sportybetug-uat1',
    [Country.ZM]: 'sportybetzm-uat1',
    [Country.INT]: 'sportybetint-uat1',
    [Country.CM]: 'sportybetint-uat1',
    [Country.ZA]: 'sportybetza-uat1',
    [Country.BR]: 'sportybetbr-uat1',
    [Country.MX]: 'sportybetmx-uat1',
    [Country.MZ]: 'sportybetmz-uat1',
  },
  firebase: {
    auth: 'pleased-firebase-uat.firebaseapp.com',
    database: 'https://pleased-fb-uat-uat1.firebaseio.com',
    projectId: 'pleased-firebase-uat',
  },
  userFieldType: {
    [Country.INT]: 'email',
    [Country.BR]: 'email',
    [Country.MX]: 'email',
  },
  languages: enableLanguages(),
  appName: 'SportyBet',
  features: {
    remedy: true,
    kycCheck: true,
    chatTriggerOpenRequestDescriptionBox: true,
    showTransactionSelectorNoneOption: true,
    showTransactionSelectorNoneOptionForAI: true,
    showTransactionSelectorNoneOptionForMetaAuto: false,
    customisation: true,
  },
  gaId: 'G-H7LZWSGKL9',
  app: {
    nativeMicroAccessCheck: true,
    deepLinkShortcut: 'sportybet://',
    deepLinkUrl: 'https://www.test.easebet.com',
    minVersionSupport: {
      articleActions: {
        MIN_IOS: '1.98.0',
        MIN_APP: '1.21.81',
      },
      articleActionsV2: {
        MIN_IOS: '1.149.0',
        MIN_APP: '1.21.177',
      },
      voiceMinimize: {
        MIN_IOS: '1.150.0',
        MIN_APP: '1.21.177',
      },
      nativeCallSupport: {
        MIN_APP: '1.42.0',
      },
    },
    nativeCallSupport: {
      [Country.ZM]: true,
    },
    abandonedCSATSupport: {
      [Country.ZM]: true,
    },
  },
  default: {
    userName: 'Sporty User',
  },
  AI: {
    rateResponse: true,
    showInBrowseOtherArticles: true,
    skipAi: {
      categoryIds: ['46'],
    },
    bypassAbTestingUserIds: [],
  },
  // REVIEW THIS CAREFULLY!!
  maintenanceMode: {
    enabled: true,
    window: {
      startDate: '2026-05-21T03:00:00Z',
      endDate: '2026-05-28T04:00:00Z',
      id: 'firebase-migration-window',
    },
    messages: SPORTYBET_MAINTENANCE_MESSAGES,
  },
};

const UAT2: HardCodedEnvironment = {
  url: 'https://uat2.pleased2.com',
  defaultBrand: 'sportybetng-uat2',
  countryToBrand: {
    [Country.NG]: 'sportybetng-uat2',
    [Country.KE]: 'sportybetke-uat2',
    [Country.GH]: 'sportybetgh-uat2',
    [Country.TZ]: 'sportybettz-uat2',
    [Country.UG]: 'sportybetug-uat2',
    [Country.ZM]: 'sportybetzm-uat2',
    // [Country.INT]: 'sportybetint-uat2',
    [Country.INT]: 'sportybet-int-uat2-sb',
    [Country.CM]: 'sportybet-cm-uat2',
    [Country.ZA]: 'sportybetza-uat2',
    [Country.BR]: 'sportybetbr-uat2',
    [Country.MX]: 'sportybetmx-uat2',
    [Country.MZ]: 'sportybetmz-uat2',
  },
  firebase: {
    auth: 'pleased-firebase-uat.firebaseapp.com',
    database: 'https://pleased-fb-uat-uat2.firebaseio.com',
    projectId: 'pleased-firebase-uat',
  },
  userFieldType: {
    [Country.INT]: 'email',
    [Country.BR]: 'email',
    [Country.MX]: 'email',
  },
  languages: enableLanguages({ [Country.INT]: 'ALL_LANGUAGES' }),
  appName: 'SportyBet',
  features: {
    remedy: true,
    kycCheck: true,
    chatTriggerOpenRequestDescriptionBox: true,
    showTransactionSelectorNoneOption: true,
    showTransactionSelectorNoneOptionForAI: true,
    showTransactionSelectorNoneOptionForMetaAuto: false,
    customisation: true,
  },
  gaId: 'G-H7LZWSGKL9',
  app: {
    nativeMicroAccessCheck: true,
    deepLinkShortcut: 'sportybet://',
    deepLinkUrl: 'https://www.test.easebet.com',
    minVersionSupport: {
      articleActions: {
        MIN_IOS: '1.98.0',
        MIN_APP: '1.21.81',
      },
      articleActionsV2: {
        MIN_IOS: '1.149.0',
        MIN_APP: '1.21.177',
      },
      voiceMinimize: {
        MIN_IOS: '1.150.0',
        MIN_APP: '1.21.177',
      },
    },
  },
  default: {
    userName: 'Sporty User',
  },
  AI: {
    rateResponse: true,
  },
  // REVIEW THIS CAREFULLY!!
  maintenanceMode: {
    enabled: true,
    window: {
      startDate: '2026-05-21T03:00:00Z',
      endDate: '2026-05-28T04:00:00Z',
      id: 'firebase-migration-window',
    },
    messages: SPORTYBET_MAINTENANCE_MESSAGES,
  },
};

const SPORTY_PRODUCTION: HardCodedEnvironment = {
  url: 'https://sportybet.pleased.com',
  defaultBrand: 'sportybettz',
  countryToBrand: {
    [Country.NG]: 'sportybetng',
    [Country.KE]: 'sportybetke',
    [Country.GH]: 'sportybetgh',
    [Country.TZ]: 'sportybettz',
    [Country.UG]: 'sportybetug',
    [Country.ZM]: 'sportybetzm',
    [Country.INT]: 'sportybetint',
    [Country.CM]: 'sportybetcm',
    [Country.ZA]: 'sportybetza',
    [Country.BR]: 'sportybetbr',
    [Country.MX]: 'sportybetmx',
    [Country.MZ]: 'sportybetmz',
  },
  firebase: {
    auth: 'pleased-firebase-enterprise.firebaseapp.com',
    database: 'https://pleased-fb-e-sportybet.firebaseio.com',
    projectId: 'pleased-firebase-enterprise',
  },
  userFieldType: {
    [Country.INT]: 'email',
    [Country.BR]: 'email',
    [Country.MX]: 'email',
  },
  // Disabled languages
  languages: enableLanguages({
    [Country.NG]: ['en'],
    // SW lang requested to be enabled on 20/01/26 by Dipesh More OPS.
    [Country.KE]: ['en', 'sw'],
    [Country.GH]: ['en'],
    // SW Lang last enabled 11/9/24, disabled on 28/10/24, Requested by Sameer Nagpal OPS.
    // Requested to be enabled on 11/06/25 by Dipesh More OPS.
    [Country.TZ]: ['en', 'sw'],
    [Country.UG]: ['en'],
    [Country.ZM]: ['en'],
    [Country.INT]: ['en', 'pt'],
  }),
  appName: 'SportyBet',
  features: {
    remedy: true,
    kycCheck: true,
    chatTriggerOpenRequestDescriptionBox: true,
    showTransactionSelectorNoneOption: true,
    showTransactionSelectorNoneOptionForAI: true,
    showTransactionSelectorNoneOptionForMetaAuto: false,
    customisation: true,
  },
  gaId: 'G-QRF0EBTL6W',
  newChatbotFlowBypassUserIds: [
    's250226062838tuid40335810',
    's250702141251puid24476131',
    '201230044915sud00049712',
    'w200923162812puid79325126',
  ],
  customisationAbPercentage: { [Country.ZM]: 0 },
  app: {
    nativeMicroAccessCheck: true,
    deepLinkShortcut: 'sportybet://',
    deepLinkUrl: 'https://sportybet.com',
    minVersionSupport: {
      articleActions: {
        MIN_IOS: '1.98.0',
        MIN_APP: '1.21.81',
      },
      articleActionsV2: {
        MIN_IOS: '1.153.0',
        MIN_APP: '1.21.184',
      },
      voiceMinimize: {
        // MIN_IOS: '1.150.0',
        MIN_APP: '1.21.177',
      },
      nativeCallSupport: {
        MIN_APP: '1.49.3',
      },
    },
    nativeCallSupport: {
      [Country.NG]: true,
      [Country.GH]: true,
      [Country.KE]: true,
      [Country.TZ]: true,
      [Country.ZM]: true,
      [Country.ZA]: true,
      [Country.MX]: true,
      [Country.CM]: true,
      [Country.BR]: true,
    },
    abandonedCSATSupport: {
      [Country.NG]: true,
      [Country.GH]: true,
      [Country.KE]: true,
      [Country.TZ]: true,
      [Country.ZM]: true,
      [Country.ZA]: true,
      [Country.MX]: true,
      [Country.CM]: true,
      [Country.BR]: true,
    },
  },
  default: {
    userName: 'Sporty User',
  },
  AI: {
    bypassAbTestingUserIds: [
      '187364',
      '14650705',
      '88058',
      '188303',
      '189558',
      '14603559',
      '131',
      '14999152',
      '688',
      '15263494',
      '5301205',
      '3860893',
      '13664630',
      '11796018',
      '13564759',
      '15469171',
      '3338513',
      '1507',
      '2247925',
      '12600609',
      '4062825',
      '14999152',
      '15333869',
      '1518',
      '111',
    ],
    showInBrowseOtherArticles: false,
  },
  selectedEntityCategory: {
    [TransactionType.Bet]: {
      [Country.NG]: '122',
      [Country.GH]: '116',
      [Country.KE]: '188',
      [Country.TZ]: '89',
      [Country.UG]: '110',
      [Country.ZM]: '95',
      [Country.INT]: '149',
      [Country.CM]: '155',
      [Country.ZA]: '171',
      [Country.BR]: '149',
      [Country.MX]: '177',
    },
  },
  // REVIEW THIS CAREFULLY!!
  maintenanceMode: {
    enabled: false,
    window: {
      startDate: '2026-05-28T03:00:00Z',
      endDate: '2026-05-28T04:00:00Z',
      id: 'firebase-migration-window-f-com',
    },
    messages: SPORTYBET_MAINTENANCE_MESSAGES,
  },
};

const FOOTBALL_STAGE: HardCodedEnvironment = {
  url: 'https://football-stage.pleased2.com',
  defaultBrand: 'football-gh-stage',
  countryToBrand: {
    [Country.NG]: 'football-ng-stage',
    [Country.KE]: 'football-ke-stage',
    [Country.GH]: 'football-gh-stage',
    [Country.TZ]: 'football-tz-stage',
    [Country.UG]: 'football-ug-stage',
    [Country.ZM]: 'football-zm-stage',
    // [Country.INT]: 'sportybetint-uat2',
    [Country.INT]: 'football-int-stage',
    [Country.CM]: 'football-cm-stage',
    [Country.ZA]: 'football-za-stage',
    [Country.BR]: 'football-br-stage',
    [Country.MX]: 'football-mx-stage',
    [Country.MZ]: 'football-mz-stage',
  },
  firebase: {
    auth: 'pleased-firebase-uat.firebaseapp.com',
    database: 'https://pleased-fb-uat-football.firebaseio.com',
    projectId: 'pleased-firebase-uat',
  },
  userFieldType: {
    [Country.INT]: 'email',
    [Country.BR]: 'email',
    [Country.MX]: 'email',
  },
  languages: enableLanguages({ [Country.TZ]: 'ALL_LANGUAGES' }),
  appName: 'Football.com',
  features: {
    remedy: true,
    chatTriggerOpenRequestDescriptionBox: true,
    showTransactionSelectorNoneOption: true,
    showTransactionSelectorNoneOptionForAI: true,
    showTransactionSelectorNoneOptionForMetaAuto: false,
    customisation: true,
  },
  customisationAbPercentage: { [Country.GH]: 100, [Country.NG]: 25 },
  landingEntryAbPercentage: { [Country.GH]: 100 },
  gaId: 'G-H7LZWSGKL9',
  app: {
    nativeMicroAccessCheck: true,
    deepLinkShortcut: 'football://',
    deepLinkUrl: 'https://encorebet.net',
    minVersionSupport: {
      articleActions: {
        MIN_APP: '10.406.561',
      },
      articleActionsV2: {
        MIN_APP: '10.505.791',
      },
    },
  },
  default: {
    userName: 'Football.com User',
  },
  AI: {},
  defaultOpenRequestResponseTime: 2,
  maintenanceMode: {
    enabled: true,
    window: {
      startDate: '2026-05-25T13:49:00Z',
      endDate: '2026-05-27T04:00:00Z',
      id: 'firebase-migration-window-1',
    },
    messages: FOOTBALL_COM_MAINTENANCE_MESSAGES,
  },
};

const FOOTBALL_PROD: HardCodedEnvironment = {
  url: 'https://football.pleased.com',
  defaultBrand: 'football-gh',
  countryToBrand: {
    [Country.NG]: 'football-ng',
    [Country.KE]: 'football-ke',
    [Country.GH]: 'football-gh',
    [Country.TZ]: 'football-tz',
    [Country.UG]: 'football-ug',
    [Country.ZM]: 'football-zm',
    [Country.INT]: 'football-int',
    [Country.CM]: 'football-cm',
    [Country.ZA]: 'football-za',
    [Country.BR]: 'football-br',
    [Country.MX]: 'football-mx',
    [Country.MZ]: 'football-mz',
  },
  firebase: {
    auth: 'pleased-firebase-enterprise.firebaseapp.com',
    database: 'https://pleased-fb-e-f-com.firebaseio.com',
    projectId: 'pleased-firebase-enterprise',
  },
  userFieldType: {
    [Country.INT]: 'email',
    [Country.BR]: 'email',
    [Country.MX]: 'email',
  },
  languages: enableLanguages(),
  appName: 'Football.com',
  features: {
    remedy: true,
    chatTriggerOpenRequestDescriptionBox: true,
    showTransactionSelectorNoneOption: true,
    showTransactionSelectorNoneOptionForAI: true,
    showTransactionSelectorNoneOptionForMetaAuto: false,
    customisation: true,
  },
  customisationAbPercentage: { [Country.GH]: 100 },
  gaId: 'G-RHR55LH9H8',
  newChatbotFlowBypassUserIds: ['K221221074520puid00028571'],
  app: {
    nativeMicroAccessCheck: true,
    deepLinkShortcut: 'football://',
    deepLinkUrl: 'https://football.com',
    minVersionSupport: {
      articleActions: {
        MIN_APP: '10.406.561',
      },
      articleActionsV2: {
        MIN_APP: '10.506.621',
      },
    },
  },
  default: {
    userName: 'Football.com User',
  },
  AI: {
    bypassAbTestingUserIds: [
      '52',
      '29',
      '146662',
      '51909',
      '540854',
      '541809',
      // Added on 29/09/25 as per request from Ops - Kanishtha Juneja
      '89420',
      '665443',
    ],
  },
  selectedEntityCategory: {
    [TransactionType.Bet]: {
      [Country.GH]: '3',
      [Country.NG]: '9',
    },
  },
  defaultOpenRequestResponseTime: 2,
  // REVIEW THIS CAREFULLY!!
  maintenanceMode: {
    enabled: false,
    window: {
      startDate: '2026-05-27T03:00:00Z',
      endDate: '2026-05-27T04:00:00Z',
      id: 'firebase-migration-window-f-com',
    },
    messages: FOOTBALL_COM_MAINTENANCE_MESSAGES,
  },
};

const ENVIRONMENTS = { FOOTBALL_PROD, FOOTBALL_STAGE, SPORTY_PRODUCTION };

/**
 * Current activated environment. When hard coded environments are removed, then this can be
 * be used as a substitute to set all the values that are globally required by the application.
 * NOTE: Only use `get()` from this module.
 */
const SESSION = {
  // Seed from the build's target environment so early reads (e.g. the guest header's default
  // userName) resolve to the correct brand before `setup()` runs. Falls back to the generic
  // prod/stage SportyBet configs when no explicit env name is baked in.
  env: ENVIRONMENTS[GLOBAL_ENV_NAME] ?? (GLOBAL_IS_PROD ? SPORTY_PRODUCTION : STAGING),
  country: '',
  brandCode: '',
  aiFlow: false,
  localeFilter: [] as LanguageSpec,
  enabledLanguages: {} as typeof LANGUAGES,
  showLanguageSelector: true,
};

const TEST_ENVIRONMENT_ROUTER: Record<Country, HardCodedEnvironment> = {
  [Country.ZM]: UAT1,
  [Country.KE]: UAT2,
  [Country.TZ]: UAT2,
  [Country.NG]: STAGING,
  [Country.GH]: UAT2,
  [Country.UG]: STAGING,
  [Country.INT]: STAGING,
  [Country.CM]: STAGING,
  [Country.ZA]: STAGING,
  [Country.BR]: STAGING,
  [Country.MX]: STAGING,
  [Country.MZ]: STAGING,
};

function getEnvironmentByCountry(country: Country | string) {
  if (ENVIRONMENTS[GLOBAL_ENV_NAME]) return ENVIRONMENTS[GLOBAL_ENV_NAME];

  if (GLOBAL_IS_PROD) {
    return SPORTY_PRODUCTION;
  }
  return TEST_ENVIRONMENT_ROUTER[country as Country];
}

/**
 * Setup the environment, should be called only once.
 */
export function setup(brandCountry: Country | string, options: InitializeOptions) {
  const { enabledLanguages } = options;
  const country = brandCountry as Country;

  // The following variables will enable the usage of `get(variable)` method.
  SESSION.env = country
    ? getEnvironmentByCountry(country)
    : {
        url: options.pleasedUrl,
        defaultBrand: options.brandCode,
        firebase: options.firebaseConfig,
        appName: options.appName,
        languages: new Proxy(
          {},
          {
            get() {
              return ['en'] as LanguageSpec;
            },
          }
        ) as Record<Country, LanguageSpec>,
      };
  SESSION.country = country;
  // Now the `get()` method is safe to use, the rest of the environment variables can be configured using `get()`

  SESSION.brandCode = (country ? get('countryToBrand')?.[country] : options.brandCode) ?? 'default';
  SESSION.localeFilter = createLocaleFilter({
    locales: enabledLanguages,
    fallback: country ? get('languages')?.[country] : ['en'],
  });
  SESSION.enabledLanguages = getEnabledLocales(SESSION.localeFilter);
  SESSION.showLanguageSelector = false; // Object.keys(SESSION.enabledLanguages).length > 1;
}

/**
 * Get the value from the current environment.
 * @param key The key to get.
 */
export function get<K extends keyof typeof SESSION>(key: K): (typeof SESSION)[K];
export function get<K extends keyof HardCodedEnvironment>(key: K): HardCodedEnvironment[K];
export function get<K extends keyof HardCodedEnvironment | keyof typeof SESSION>(
  key: K
): K extends keyof HardCodedEnvironment
  ? HardCodedEnvironment[K]
  : K extends keyof typeof SESSION
    ? (typeof SESSION)[K]
    : undefined {
  if (!SESSION || !SESSION.env) {
    devLog.error(`[ENVIRONMENT] SESSION.env is not set, but required to get the value for key "${key}"`);
    return undefined as any;
  }

  const value = SESSION[key as keyof typeof SESSION] || SESSION.env[key as keyof HardCodedEnvironment];
  if (value && typeof value === 'object') {
    if (Array.isArray(value)) return [...value] as any;
    return Object.assign({}, value) as any;
  }
  return value as any;
}

export function isLocaleEnabled(locale: string) {
  const toCheck = locale === 'pt-br' ? 'pt' : locale;
  return Boolean(get('enabledLanguages')?.[toCheck as SupportedLanguages]);
}

export function getMaintenanceMessage(locale: string): { strong: string; weak: string; email: string } | null {
  const maintenanceModeConfig = get('maintenanceMode');
  if (!maintenanceModeConfig?.enabled || !maintenanceModeConfig.messages) return null;

  const country = get('country') as Country;
  const message = getMaintenanceMessageFromConfig(country, locale, maintenanceModeConfig.messages);
  return message;
}

function getEnabledLocales(filter: LanguageSpec, allLanguages = LANGUAGES) {
  if (filter === 'ALL_LANGUAGES') return allLanguages;

  return Object.fromEntries(
    // For now, force EN language.
    Object.entries(allLanguages).filter(([locale]) => locale === 'en' || filter?.includes(locale as SupportedLanguages))
  ) as typeof LANGUAGES;
}

function createLocaleFilter({ locales = [], fallback }: { locales?: string[]; fallback: LanguageSpec }): LanguageSpec {
  if (!locales?.length || !Array.isArray(locales)) return fallback;
  // Normalize the filter. Keep invalid values for now, better for debugging
  return locales.map((s) => s?.trim?.() || '').map((l) => (l === 'pt-br' ? 'pt' : l)) as LanguageSpec;
}
